/**
 * Empty-area-click deselect for the Map (storyboard Flow Q §"Frame 5 —
 * Operator deselects"; REQ-UI-051; ADR-024 §5).
 *
 * Flow Q Frame 5: "The operator clicks an empty area on the map (not on a
 * marker). All three panels deselect." Clicking the basemap — anywhere that
 * is not a platform marker/cluster — clears the synchronized-selection bus,
 * so the Map selection ring, the Platforms row highlight, and the Entity
 * Details panel all reset together.
 *
 * # How it coexists with the entity click handlers
 *
 * The platform-marker and cluster click handlers are registered layer-scoped
 * (``map.on("click", layerId, fn)``). The deselect
 * handler is registered map-wide (``map.on("click", fn)``) and fires for
 * every click, including those that also hit an entity layer. To avoid
 * clearing on an entity click it decides via :func:`isBackgroundClick`,
 * which queries the rendered features under the cursor — so the outcome is
 * independent of the order MapLibre invokes the handlers: a click that lands
 * on an entity layer is handled by that layer's select handler and ignored
 * here; a click on the bare basemap clears.
 */
import type { PointLike } from "maplibre-gl";
import { CLUSTER_COUNT_LAYER_ID, CLUSTER_LAYER_ID } from "@/map/cluster-styling";
import { SNAPSHOT_LABEL_LAYER_ID, SNAPSHOT_LAYER_ID } from "@/map/snapshot-layer-impl";
import { DETECTIONS_LAYER_ID } from "@/map/detection-layer-impl";
import { ALERTS_LAYER_ID } from "@/map/alert-layer-impl";
import { OBJECT_TRACK_LAYER_ID, trackHitBox } from "@/map/object-track-layer-impl";

/**
 * The layers whose features represent a clickable / interactive entity:
 * platform markers + their labels, cluster bubbles + their counts, and the
 * detection + alert glyph layers. A click landing on any of these is an
 * entity interaction, not a background click, and must not deselect.
 *
 * # Why the detection / alert glyph layers MUST be listed here
 *
 * Each glyph layer registers its own layer-scoped select handler
 * (``map.on("click", DETECTIONS_LAYER_ID, …)`` in :file:`detection-layer.tsx`;
 * likewise :file:`alert-layer.tsx`) that writes ``{kind, id}`` to the
 * selection bus. The map-wide background-deselect handler (:file:`Map.tsx`)
 * fires on the *same* click; if it judged the glyph hit a "background" click
 * it would ``clear()`` the selection the glyph handler just set, so the
 * Map↔Detections / Map↔Alerts sync would silently never stick — while
 * Map↔Platforms worked, because the snapshot layer is listed here. Listing
 * the glyph layers makes :func:`isBackgroundClick` recognise a glyph hit as an
 * entity interaction and leave the selection alone. A click that misses
 * *every* entity layer still clears (Flow Q Frame 5; REQ-UI-051/063).
 *
 * (Pre-PR6 only the single Event glyph layer existed and was likewise not
 * registered here; the ADR-032 cascade made detections + alerts independently
 * clickable, which is what surfaced the regression.)
 *
 * The object-track **line** layer is listed for the same reason (REQ-UI-171;
 * ADR-041 D-5): :file:`object-track-layer.tsx` originates a ``track``
 * selection from a line click, and leaving the layer out let this handler
 * clear that selection on the same click — found by the §5.11 live pass on
 * 2026-09-30, invisible to the line handler's own unit tests. The track-tail
 * and per-platform trail layers are not listed: they have no click handler,
 * so a click on them is a background click.
 */
export const ENTITY_SELECTION_LAYERS: readonly string[] = [
  SNAPSHOT_LAYER_ID,
  SNAPSHOT_LABEL_LAYER_ID,
  CLUSTER_LAYER_ID,
  CLUSTER_COUNT_LAYER_ID,
  DETECTIONS_LAYER_ID,
  ALERTS_LAYER_ID,
  OBJECT_TRACK_LAYER_ID,
];

/**
 * The slice of the MapLibre map this module needs. Declared structurally so
 * the helper is trivially mockable in tests; a real ``maplibre-gl`` ``Map``
 * satisfies it.
 */
export interface DeselectQueryMap {
  getLayer(id: string): unknown;
  queryRenderedFeatures(
    geometry: PointLike | [PointLike, PointLike],
    options: { layers: string[] },
  ): unknown[];
}

/**
 * The entity layers whose click target is the **exact** cursor point — every
 * glyph, marker, label and cluster bubble. They are drawn big enough to aim
 * at, so a tolerance would only let them steal clicks from each other and
 * from the track line beneath them.
 *
 * Exported because the track line's own click handler consults the same list
 * to decide precedence: a glyph under the cursor wins over the line running
 * beneath it, which is the rule that keeps a head-glyph click selecting its
 * detection now that the line's corridor is wider than the glyph
 * (REQ-UI-199). Sharing the list is what stops the two decisions drifting.
 */
export const GLYPH_PRECEDENCE_LAYERS: readonly string[] = ENTITY_SELECTION_LAYERS.filter(
  (id) => id !== OBJECT_TRACK_LAYER_ID,
);

/**
 * Whether a click at ``point`` should clear the selection — i.e. it landed
 * on none of the entity layers present in the style.
 *
 * ``queryRenderedFeatures`` throws when passed a layer id absent from the
 * style (the events / cluster layers aren't added until they have data), so
 * the candidate layers are filtered to those currently present first; if
 * none are present the click is necessarily background.
 *
 * # Why the track line is queried differently
 *
 * The glyph layers are asked about the exact cursor point. The object-track
 * **line** is asked about the 24 px corridor its own click handler uses
 * (:data:`object-track-layer-impl.OBJECT_TRACK_HIT_TOLERANCE_PX`), because the
 * two decisions must be made on the same hit. A line is 2 px wide, so a
 * corridor is what makes it selectable at all (REQ-UI-199) — and if this
 * handler kept point-querying it, every click that selected a track *from the
 * corridor but off the rendered line* would also read as background here and
 * clear the selection the corridor had just set, on the same click. That is
 * the fourth edition of the race described above, and the reason the tolerance
 * is one exported constant rather than a number in each file.
 */
export function isBackgroundClick(map: DeselectQueryMap, point: PointLike): boolean {
  const points = GLYPH_PRECEDENCE_LAYERS.filter((id) => map.getLayer(id) !== undefined);
  if (points.length > 0 && map.queryRenderedFeatures(point, { layers: points }).length > 0) {
    return false;
  }
  if (map.getLayer(OBJECT_TRACK_LAYER_ID) === undefined) {
    return true;
  }
  const box = trackHitBox(asXY(point));
  return map.queryRenderedFeatures(box, { layers: [OBJECT_TRACK_LAYER_ID] }).length === 0;
}

/** MapLibre's ``PointLike`` in the ``{x, y}`` form :func:`trackHitBox` takes. */
function asXY(point: PointLike): { x: number; y: number } {
  if (Array.isArray(point)) {
    return { x: point[0], y: point[1] };
  }
  return { x: point.x, y: point.y };
}
